/**
 * Shared Grow UI constants (client). Mirrors functions/growLinks.js — keep in sync.
 * grow_integration.md §2, §3, §5.
 */

export const GROW_METHODS = Object.freeze({
  CREDIT_CARD: 1,
  PAYBOX: 5,
  BIT: 6,
  APPLE_PAY: 13,
  GOOGLE_PAY: 14,
  BANK_TRANSFER: 15,
});

/** Methods a clinic may toggle in v1 (single payment, no installments / bank transfer). */
export const GROW_METHOD_OPTIONS = Object.freeze([
  { id: GROW_METHODS.CREDIT_CARD, label: "כרטיס אשראי", hint: "ויזה, מאסטרקארד, ישראכרט" },
  { id: GROW_METHODS.BIT, label: "Bit", hint: "עד 5,000 ₪ לעסקה" },
  { id: GROW_METHODS.APPLE_PAY, label: "Apple Pay", hint: "מכשירי Apple בלבד" },
  { id: GROW_METHODS.GOOGLE_PAY, label: "Google Pay", hint: "דפדפן Chrome / Android" },
]);

export const GROW_METHOD_LABELS = Object.freeze({
  1: "אשראי",
  5: "PayBox",
  6: "Bit",
  13: "Apple Pay",
  14: "Google Pay",
  15: "העברה בנקאית",
});

export const growMethodLabel = (id) => GROW_METHOD_LABELS[Number(id)] || "אחר";

export const GROW_VAT_OPTIONS = Object.freeze([
  { value: 1, label: "עוסק מורשה / חברה", hint: "המחיר כולל מע\"מ" },
  { value: 3, label: "עוסק פטור / עמותה", hint: "ללא מע\"מ" },
]);

/** Default non-secret settings — identical to functions/growLinks.js DEFAULT_GROW_SETTINGS. */
export const GROW_DEFAULT_SETTINGS = Object.freeze({
  methods: [1, 6, 13, 14],
  autoSendOnCompletion: false,
  autoSendDelayMinutes: 0,
  sendImmediately: true,
  sendReceiptToPatient: false,
  autoRemindPatient: false,
  linkExpiryDays: 14,
  reminderAfterDays: 3,
  invoiceMode: "grow",
  branding: {},
});

/** clinic.vatType ('exempt' | 'licensed' | 'company') → Grow vatType. */
export const clinicVatToGrow = (vatType) => (vatType === "exempt" ? 3 : 1);

/** Build the form state from clinic + saved settings. */
export const buildGrowSettingsState = (clinicProfile, saved = {}) => {
  const s = { ...GROW_DEFAULT_SETTINGS, ...(saved || {}) };
  return {
    methods: Array.isArray(s.methods) && s.methods.length ? s.methods.map(Number) : [...GROW_DEFAULT_SETTINGS.methods],
    vatType: s.vatType === 3 || s.vatType === 1 ? s.vatType : clinicVatToGrow(clinicProfile?.vatType),
    autoSendOnCompletion: s.autoSendOnCompletion === true,
    sendImmediately: s.sendImmediately !== false,
    sendReceiptToPatient: s.sendReceiptToPatient === true,
    autoRemindPatient: s.autoRemindPatient === true,
    autoSendDelayMinutes: Number.isInteger(Number(s.autoSendDelayMinutes)) ? Number(s.autoSendDelayMinutes) : 0,
    linkExpiryDays: Number(s.linkExpiryDays) > 0 ? Number(s.linkExpiryDays) : 14,
    reminderAfterDays: Number.isInteger(Number(s.reminderAfterDays)) ? Number(s.reminderAfterDays) : 3,
    invoiceMode: s.invoiceMode === "none" ? "none" : "grow",
    branding: {
      backgroundColor: s.branding?.backgroundColor || "",
      buttonColor: s.branding?.buttonColor || "",
      paymentButtonText: s.branding?.paymentButtonText || "",
    },
  };
};

/** Strip empty branding fields before sending to the callable. */
export const serializeGrowSettings = (form) => {
  const branding = {};
  if (/^#[0-9A-Fa-f]{6}$/.test(form.branding?.backgroundColor || "")) branding.backgroundColor = form.branding.backgroundColor;
  if (/^#[0-9A-Fa-f]{6}$/.test(form.branding?.buttonColor || "")) branding.buttonColor = form.branding.buttonColor;
  if ((form.branding?.paymentButtonText || "").trim()) branding.paymentButtonText = form.branding.paymentButtonText.trim().slice(0, 20);
  return {
    methods: form.methods,
    vatType: form.vatType,
    autoSendOnCompletion: form.autoSendOnCompletion === true,
    sendImmediately: form.sendImmediately !== false,
    sendReceiptToPatient: form.sendReceiptToPatient === true,
    autoRemindPatient: form.autoRemindPatient === true,
    autoSendDelayMinutes: Number(form.autoSendDelayMinutes) || 0,
    linkExpiryDays: Number(form.linkExpiryDays) || 14,
    reminderAfterDays: Number(form.reminderAfterDays) || 0,
    invoiceMode: form.invoiceMode,
    branding,
  };
};

export const GROW_BACKOFFICE_URL = "https://meshulam.co.il/business/login";
export const GROW_ID_PATTERN = /^[A-Za-z0-9_-]{4,64}$/;

/** Hebrew copy for link statuses (used by Phase 5 chips too). */
export const GROW_LINK_STATUS_LABELS = Object.freeze({
  creating: "נוצר…",
  created: "נוצר",
  sent: "נשלח",
  paid: "שולם",
  expired: "פג תוקף",
  cancelled: "בוטל",
  failed: "נכשל",
});
